import { Field, useFormikContext } from 'formik';
import type { FieldInputProps, FieldMetaProps, FormikState } from 'formik';
import { forwardRef, useCallback } from 'react';
import type { ChangeEvent, FocusEvent } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { TextAreaInput } from '@hivebrite/design-system/Inputs';

import shouldDisplayError from 'hb-react/shared/components/formik/utils/shouldDisplayError';

import type { TextAreaProps } from './types';
import { validateTextarea } from './validate';

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextAreaProps<HTMLTextAreaElement> & {
    minHeight?: number;
    alwaysDisplayError?: boolean;
  }
>(
  (
    {
      ariaDescribedBy,
      ariaLabel,
      disabled,
      alwaysDisplayError,
      icon,
      id,
      info,
      infoIcon,
      label,
      labelMultiline,
      minHeight,
      name,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      tooltip,
      validate: {
        excludeRegex,
        maxEmailsInline,
        maxLength,
        required,
        validEmailsInline,
      } = {},
      wrapperProps,
    },
    ref,
  ) => {
    const formik = useFormikContext();
    const htmlId = id || name;

    const handleBlur = useCallback(
      (e: FocusEvent<HTMLTextAreaElement>) => {
        formik.handleBlur(e);
        onBlur?.(e);
      },
      [formik, onBlur],
    );

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        formik.handleChange(e);
        onChange?.(e.target.value);
      },
      [formik, onChange],
    );

    const handleValidate = useCallback(
      (value: string) =>
        validateTextarea({
          excludeRegex,
          maxEmailsInline,
          maxLength,
          required,
          validEmailsInline,
          value,
        }),
      [excludeRegex, maxEmailsInline, maxLength, required, validEmailsInline],
    );

    return (
      <Box mb={6} {...wrapperProps}>
        <Field name={name} validate={handleValidate}>
          {({
            field,
            form,
            meta,
          }: {
            field: FieldInputProps<any>;
            form: FormikState<any>;
            meta: FieldMetaProps<any>;
          }) => (
            <TextAreaInput
              {...field}
              ariaDescribedBy={ariaDescribedBy}
              ariaLabel={ariaLabel}
              dataTestId="formik-textarea"
              disabled={disabled}
              error={shouldDisplayError({ meta, form, alwaysDisplayError })}
              icon={icon}
              id={htmlId}
              info={info}
              infoIcon={infoIcon}
              label={label}
              labelMultiline={labelMultiline}
              minHeight={minHeight}
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={onFocus}
              placeholder={placeholder}
              ref={ref}
              required={required}
              tooltip={tooltip}
            />
          )}
        </Field>
      </Box>
    );
  },
);

export default TextArea;
